import { Content } from '@prismicio/client'
import { SliceComponentProps } from '@prismicio/react'
import styled from '@emotion/styled'
import React from 'react'
import { isFilled } from '@prismicio/client'
import { PrismicRichText } from '@prismicio/react'
import { PrismicNextImage } from '@prismicio/next'
import { H1, H2, H3, H4, H5, H6, Text } from 'library/atoms/Typography'
import { useRouter } from 'next/router'
import { titleize } from 'shared/helpers/stringUtilities'
import ContentHero from 'library/molecules/ContentHero'

type Simplify<T> = { [KeyType in keyof T]: T[KeyType] }

/**
 * Props for `ContentWithHexagonImages`.
 */
export type ContentWithHexagonImagesProps =
  SliceComponentProps<Content.ContentWithHexagonImagesSlice>

type SliceContent =
  | Simplify<Content.ContentWithHexagonImagesSliceDefaultPrimary>
  | Simplify<Content.ContentWithHexagonImagesSliceSharedPrimary>
  | null

const BACKGROUND_COLORS = {
  none: 'transparent',
  grey: '#f9f9f9',
  blue: 'rgba(231,241,255,1)',
  pink: 'rgb(255, 233, 233)',
  yellow: 'rgb(255, 247, 226)'
}

const RichText = ({
  content,
  substitution
}: {
  content: any
  substitution: string
}) => {
  return (
    <PrismicRichText
      field={content}
      components={{
        heading1: ({ children }) => <H1>{children}</H1>,
        heading2: ({ children }) => <H2>{children}</H2>,
        heading3: ({ children }) => <H3>{children}</H3>,
        heading4: ({ children }) => <H4>{children}</H4>,
        heading5: ({ children }) => <H5>{children}</H5>,
        heading6: ({ children }) => <H6>{children}</H6>,
        paragraph: ({ children }) => (
          <Text fontStyle="p1" color="secondary">
            {children}
          </Text>
        ),
        span: ({ text, key }) => {
          return (
            <React.Fragment key={key}>
              {text.replace('{uid}', substitution)}
            </React.Fragment>
          )
        }
      }}
    />
  )
}

/**
 * Component for "ContentWithHexagonImages" Slices.
 */
const ContentWithHexagonImages = ({
  slice
}: ContentWithHexagonImagesProps): JSX.Element => {
  const { query } = useRouter()
  const uid = query.uid ? titleize(query.uid as string) : ''

  // Here we are trying to get it so that sliceContent contains the same structure or
  // null the typing is a little tricky here because of the shared variation which
  // uses a content relationship. Its either a slice data type if primary or a content
  // hero document if shared variation
  let sliceContent: SliceContent = null
  if (slice.variation === 'shared') {
    sliceContent = isFilled.contentRelationship(slice.primary.shared_content)
      ? (slice.primary.shared_content?.data as Content.ContentHeroDocumentData)
      : null
  } else {
    sliceContent = slice.primary
  }

  if (!sliceContent) {
    return <></>
  }

  const backgroundColor = isFilled.keyText(sliceContent.backgroundcolor)
    ? BACKGROUND_COLORS[
        sliceContent.backgroundcolor as keyof typeof BACKGROUND_COLORS
      ]
    : BACKGROUND_COLORS['none']

  return (
    <ContentHero
      backgroundColor={backgroundColor}
      title={<RichText content={sliceContent.title} substitution={uid} />}
      content={<RichText content={sliceContent.content} substitution={uid} />}
      links={
        isFilled.group(sliceContent.links)
          ? sliceContent.links.map((item: any) => ({
              text: item.text,
              link: item.link.url
            }))
          : []
      }
      list={
        isFilled.group(sliceContent.list)
          ? sliceContent.list.map((item: any) => ({
              icon: (
                <PrismicNextImage
                  field={item.icon}
                  height={40}
                  width={40}
                  imgixParams={{ w: 40, fit: 'clip' }}
                />
              ),
              text: item.text
            }))
          : []
      }
      image={
        sliceContent?.images?.[0]?.image && (
          <MainImage
            field={sliceContent.images[0].image}
            imgixParams={{
              w: 1400,
              h: 1400,
              fit: 'clip'
            }}
          />
        )
      }
    />
  )
}

export default ContentWithHexagonImages

const MainImage = styled(PrismicNextImage)(() => ({
  width: '100%',
  height: 'auto'
}))
